import { LIMITS, isSenderBox, type SignatureAnchor } from '@worki/shared';

/**
 * SIG-003 — what the copy button on a placed box makes: the same box, for the
 * next signer. Away from the DOM for the reason `anchors.ts` is.
 *
 * The next signer is the one after the last signer with a box on the document,
 * whichever box is copied. It used to be the one after the copied box's own
 * signer, and the box people copy is the first one — the one on the form's
 * first line — so every copy was another box for signer 2 (Nitzan, 16.09). The
 * compose screen adds a signer for each number the boxes reach, so the numbered
 * button, which cycles through the signers there are, stayed at 2 as well.
 *
 * `null` when there is nothing to copy: every signer a document may have
 * already has a box, the document has as many places as it may, or there is no
 * box at `index`. The placer disables the button on exactly this, so what the
 * button allows and what pressing it does cannot come apart again.
 *
 * Nor is there for a box the sender fills in or signs (1.1): it is no signer's,
 * so there is no next signer to make one for, and a copy numbered for somebody
 * would add a signer to the compose screen that nothing on the page asks to sign.
 */
export function copyForNextSigner(anchors: SignatureAnchor[], index: number): SignatureAnchor[] | null {
  const anchor = anchors[index];
  if (!anchor || isSenderBox(anchor) || anchors.length >= LIMITS.maxSignatureAnchors) return null;

  /* Absent is the first signer, as it is everywhere a box is read: a box placed
     before there could be a second signer is theirs. */
  const signer = anchors.reduce((last, current) => Math.max(last, current.signer ?? 1), 1) + 1;
  if (signer > LIMITS.maxSigners) return null;

  return [
    ...anchors,
    {
      ...anchor,
      signer,
      // Just under the box it was copied from, and never off the foot of the page.
      y: Math.min(1 - anchor.height, anchor.y + anchor.height + 0.02),
    },
  ];
}
